import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearch, useRouter, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '@/api/client';
import type { Tenant, TenantGovId, RentEntry, Payment, AuditLogEntry, UpdateTenantRequest, Gender, Hostel, Complaint, ComplaintCategory, ComplaintStatus } from '@/api/types';
import { User, Receipt, Clock, Edit3, X, Check, Download, Upload, Camera, Printer, ArrowLeftRight, Move, LogIn, LogOut, FileText, Bell, Send, AlertCircle } from 'lucide-react';
import { SignTermsOnBehalfInline } from '@/components/SignTermsOnBehalfInline';
import { SwapBedPanel } from '@/features/beds/SwapBedPanel';
import { TransferBedPanel } from '@/features/beds/TransferBedPanel';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination, paginateArray } from '@/hooks/usePagination';
import { paymentMethodLabel } from '@/lib/labels';
import { sanitizePhoneInput } from '@/lib/utils';

type Tab = 'profile' | 'rent' | 'audit' | 'complaints';

const RENT_STATUS_COLORS: Record<string, string> = {
  PENDING:  'bg-yellow-100 text-yellow-800',
  PAID:     'bg-green-100 text-green-800',
  PARTIAL:  'bg-blue-100 text-blue-800',
  OVERDUE:  'bg-red-100 text-red-800',
  WAIVED:   'bg-gray-100 text-gray-600',
};

const TENANT_STATUS_COLORS: Record<string, string> = {
  ACTIVE:      'bg-green-100 text-green-800',
  RESERVED:    'bg-amber-100 text-amber-800',
  CHECKED_OUT: 'bg-gray-100 text-gray-600',
  CANCELLED:   'bg-red-100 text-red-700',
};

function Field({ label, value }: { label: string; value?: string | number | null }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm text-gray-900 break-words">{value}</p>
    </div>
  );
}

function PhotoUploader({ tenantId, photoUrl, name }: { tenantId: string; photoUrl: string; name: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [preview, setPreview] = useState(photoUrl);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  // keep preview in sync if parent re-fetches newer data
  useEffect(() => { setPreview(photoUrl); }, [photoUrl]);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, showCamera]);

  const resizeToBase64 = (file: File): Promise<string> =>
    new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const size = Math.min(img.width, img.height);
        const canvas = document.createElement('canvas');
        canvas.width = 300; canvas.height = 300;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 300, 300);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = url;
    });

  const savePhoto = async (b64: string) => {
    setPreview(b64);
    setSaving(true);
    setSaved(false);
    setErr('');
    try {
      await client.patch(`/api/tenants/${tenantId}/photo`, { photoUrl: b64 });
      qc.setQueryData<Tenant>(['tenant', tenantId], (old) =>
        old ? { ...old, photoUrl: b64 } : old
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setPreview(photoUrl);
      setErr((e as Error)?.message ?? t('owner.photoSaveFailed', 'Photo save failed — please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await savePhoto(await resizeToBase64(file));
    e.target.value = '';
  };

  const openCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 640 } });
      setStream(s);
      setShowCamera(true);
    } catch {
      fileRef.current?.click();
    }
  };

  const capture = () => {
    const video = videoRef.current; const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = 300; canvas.height = 300;
    const ctx = canvas.getContext('2d')!;
    const size = Math.min(video.videoWidth, video.videoHeight);
    ctx.drawImage(video, (video.videoWidth - size) / 2, (video.videoHeight - size) / 2, size, size, 0, 0, 300, 300);
    const b64 = canvas.toDataURL('image/jpeg', 0.85);
    closeCamera();
    void savePhoto(b64);
  };

  const closeCamera = () => {
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
    setShowCamera(false);
  };

  return (
    <>
      <div className="flex flex-col items-center gap-2">
        <div className="relative w-28 h-28 rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100 shadow-sm">
          {preview
            ? <img src={preview} alt="Profile" className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-4xl">{name.charAt(0).toUpperCase()}</div>
          }
          {saving && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <p className="text-[10px] text-gray-400 font-medium">{t('owner.photo2x2', '2×2 Photo')}</p>
        {saved && <p className="text-[10px] text-green-600 font-medium">✓ {t('owner.saved', 'Saved')}</p>}
        {err && <p className="text-[10px] text-red-500 text-center max-w-[120px]">{err}</p>}
        <div className="flex gap-1.5">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={saving}
            className="flex items-center gap-1 text-xs border border-gray-300 px-2.5 py-1 rounded-lg hover:bg-gray-50 text-gray-600 disabled:opacity-50">
            <Upload size={11} /> {t('owner.upload', 'Upload')}
          </button>
          <button type="button" onClick={openCamera} disabled={saving}
            className="flex items-center gap-1 text-xs border border-teal-300 text-teal-700 px-2.5 py-1 rounded-lg hover:bg-teal-50 disabled:opacity-50">
            <Camera size={11} /> {t('owner.camera', 'Camera')}
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {showCamera && (
        <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
          <div className="bg-chrome-page rounded-2xl overflow-hidden w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="font-semibold text-sm text-gray-800">{t('owner.takePhoto', 'Take Photo')}</p>
              <button type="button" onClick={closeCamera} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <video ref={videoRef} className="w-full aspect-square object-cover rounded-xl bg-black" muted playsInline />
              <button type="button" onClick={capture}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2.5 rounded-xl text-sm font-semibold transition">
                📸 {t('owner.capturePhoto', 'Capture Photo')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function GovIdPhotoSlot({ tenantId, govIdId, photoUrl, slot }: {
  tenantId: string; govIdId: string; photoUrl: string; slot: 'front' | 'back';
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [preview, setPreview] = useState(photoUrl);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const field = slot === 'front' ? 'frontPhotoUrl' : 'backPhotoUrl';
  const label = slot === 'front' ? t('owner.front', 'Front') : t('owner.back', 'Back');

  useEffect(() => { setPreview(photoUrl); }, [photoUrl]);
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, showCamera]);

  const resizeToBase64 = (file: File): Promise<string> =>
    new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 800; canvas.height = 600;
        const ctx = canvas.getContext('2d')!;
        const scale = Math.min(800 / img.width, 600 / img.height);
        const sw = img.width * scale; const sh = img.height * scale;
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 800, 600);
        ctx.drawImage(img, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
      img.src = url;
    });

  const save = async (b64: string) => {
    setPreview(b64); setSaving(true); setSaved(false); setErr('');
    try {
      await client.put(`/api/tenants/${tenantId}/gov-ids/${govIdId}`, { [field]: b64 });
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { setPreview(photoUrl); setErr((e as Error)?.message ?? t('owner.saveFailed', 'Save failed')); }
    finally { setSaving(false); }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    await save(await resizeToBase64(file)); e.target.value = '';
  };

  const openCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: 1280, height: 720 } });
      setStream(s); setShowCamera(true);
    } catch { fileRef.current?.click(); }
  };

  const capture = () => {
    const video = videoRef.current; const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = 800; canvas.height = 600;
    const ctx = canvas.getContext('2d')!;
    const scale = Math.min(800 / video.videoWidth, 600 / video.videoHeight);
    const sw = video.videoWidth * scale; const sh = video.videoHeight * scale;
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 800, 600);
    ctx.drawImage(video, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
    closeCamera(); void save(canvas.toDataURL('image/jpeg', 0.9));
  };

  const closeCamera = () => { stream?.getTracks().forEach(t => t.stop()); setStream(null); setShowCamera(false); };

  return (
    <>
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-gray-500">{label} Side</p>
        {preview ? (
          <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style={{ aspectRatio: '4/3' }}>
            <img src={preview} alt={`ID ${label}`} className="w-full h-full object-contain" />
            {saving && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-1 text-gray-300" style={{ aspectRatio: '4/3' }}>
            <Camera size={22} /><p className="text-[11px]">{t('owner.noPhoto', 'No photo')}</p>
          </div>
        )}
        {saved && <p className="text-[10px] text-green-600">✓ {t('owner.saved', 'Saved')}</p>}
        {err && <p className="text-[10px] text-red-500">{err}</p>}
        <div className="flex gap-1.5">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={saving}
            className="flex items-center gap-1 text-[11px] border border-gray-300 px-2 py-1 rounded hover:bg-gray-50 disabled:opacity-50">
            <Upload size={10} /> {t('owner.upload', 'Upload')}
          </button>
          <button type="button" onClick={openCamera} disabled={saving}
            className="flex items-center gap-1 text-[11px] border border-teal-300 text-teal-700 px-2 py-1 rounded hover:bg-teal-50 disabled:opacity-50">
            <Camera size={10} /> {t('owner.scan', 'Scan')}
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        <canvas ref={canvasRef} className="hidden" />
      </div>
      {showCamera && (
        <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
          <div className="bg-chrome-page rounded-2xl overflow-hidden w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="font-semibold text-sm">{t('owner.scanIdSide', 'Scan ID — {{side}} Side', { side: label })}</p>
              <button type="button" onClick={closeCamera}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <video ref={videoRef} className="w-full rounded-xl bg-black" style={{ aspectRatio: '4/3' }} muted playsInline />
              <button type="button" onClick={capture} className="w-full bg-teal-600 text-white py-2.5 rounded-xl text-sm font-semibold">
                📷 {t('owner.captureSide', 'Capture {{side}} Side', { side: label })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function GovIdEntry({ tenantId, entry }: { tenantId: string; entry: TenantGovId }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [typeVal, setTypeVal] = useState(entry.govIdType ?? '');
  const [deleting, setDeleting] = useState(false);

  const updateType = async (val: string) => {
    setTypeVal(val);
    await client.put(`/api/tenants/${tenantId}/gov-ids/${entry.id}`, { govIdType: val || null });
    void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await client.delete(`/api/tenants/${tenantId}/gov-ids/${entry.id}`);
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    } finally { setDeleting(false); }
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3 relative bg-gray-50/40">
      <button type="button" onClick={remove} disabled={deleting}
        className="absolute top-3 right-3 text-gray-400 hover:text-red-500 disabled:opacity-40">
        <X size={15} />
      </button>
      <Select
        value={typeVal}
        onChange={e => void updateType(e.target.value)}
        className="w-auto mr-6"
        options={[
          { value: '', label: t('owner.idTypePlaceholder', '— ID Type —') },
          ...['AADHAAR', 'PASSPORT', 'DRIVING_LICENSE', 'VOTER_ID', 'NATIONAL_ID', 'OTHER'].map(idType => ({ value: idType, label: idType })),
        ]}
      />
      <div className="grid grid-cols-2 gap-3">
        <GovIdPhotoSlot tenantId={tenantId} govIdId={entry.id} photoUrl={entry.frontPhotoUrl ?? ''} slot="front" />
        <GovIdPhotoSlot tenantId={tenantId} govIdId={entry.id} photoUrl={entry.backPhotoUrl ?? ''} slot="back" />
      </div>
    </div>
  );
}

function GovIdList({ tenantId, govIds }: { tenantId: string; govIds: TenantGovId[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const addEntry = async () => {
    setAdding(true);
    try {
      await client.post(`/api/tenants/${tenantId}/gov-ids`, {});
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    } finally { setAdding(false); }
  };

  return (
    <div className="space-y-3">
      {govIds.map(g => <GovIdEntry key={g.id} tenantId={tenantId} entry={g} />)}
      <button type="button" onClick={addEntry} disabled={adding}
        className="flex items-center gap-1.5 text-sm text-teal-700 border border-teal-300 px-3 py-1.5 rounded-lg hover:bg-teal-50 disabled:opacity-50">
        {adding ? <div className="w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" /> : '＋'}
        {t('owner.addAnotherId', 'Add Another ID')}
      </button>
    </div>
  );
}

function printProfile(tenant: Tenant) {
  const row = (label: string, value?: string | null) =>
    value ? `<tr><td style="color:#6b7280;padding:5px 12px 5px 0;font-size:12px;white-space:nowrap;vertical-align:top">${label}</td><td style="padding:5px 0;font-size:13px;font-weight:500">${value}</td></tr>` : '';

  const section = (title: string, rows: string) =>
    `<div style="margin-bottom:18px"><p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#9ca3af;margin:0 0 6px">${title}</p><table style="border-collapse:collapse;width:100%">${rows}</table></div>`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Profile – ${tenant.fullName}</title>
<style>
  @page { size: A4; margin: 20mm; }
  * { box-sizing:border-box; margin:0; padding:0; font-family:Arial,sans-serif; }
  body { color:#111; font-size:13px; }
  h1 { font-size:22px; font-weight:700; }
  .sub { font-size:12px; color:#6b7280; margin-top:3px; }
  hr { border:none; border-top:1px solid #e5e7eb; margin:14px 0; }
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
  .badge { display:inline-block; padding:2px 10px; border-radius:20px; font-size:11px; font-weight:600;
    background:#d1fae5; color:#065f46; }
  .footer { margin-top:40px; padding-top:12px; border-top:1px solid #e5e7eb; font-size:11px; color:#9ca3af; text-align:center; }
</style></head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px">
  <div>
    <h1>${tenant.fullName}</h1>
    <p class="sub">${tenant.phone}${tenant.email ? ' · ' + tenant.email : ''}</p>
    <p style="margin-top:8px"><span class="badge">${tenant.status.replace('_',' ')}</span></p>
  </div>
  ${tenant.photoUrl ? `<img src="${tenant.photoUrl}" style="width:90px;height:90px;object-fit:cover;border-radius:8px;border:2px solid #e5e7eb" alt="Photo"/>` : `<div style="width:90px;height:90px;border-radius:8px;border:2px solid #e5e7eb;background:#f0fdfa;display:flex;align-items:center;justify-content:center;font-size:36px;font-weight:700;color:#0d9488">${tenant.fullName.charAt(0).toUpperCase()}</div>`}
</div>
<hr/>
<div class="two-col">
  <div>
    ${section('Personal', [
      row('Full Name', tenant.fullName),
      row('Phone', tenant.phone),
      row('Email', tenant.email),
      row('Date of Birth', tenant.dateOfBirth),
      row('Gender', tenant.gender),
      row('Occupation', tenant.occupation),
      row('Company / Institution', tenant.companyOrInstitution),
    ].join(''))}
    ${section('ID & Financials', [
      row('Gov ID Type', tenant.govIdType),
      row('Monthly Rent', tenant.monthlyRent ? '₹' + Number(tenant.monthlyRent).toLocaleString() : null),
      row('Security Deposit', tenant.securityDeposit ? '₹' + Number(tenant.securityDeposit).toLocaleString() : null),
      row('Joining Date', tenant.joiningDate),
      row('Expected Checkout', tenant.expectedCheckoutDate),
    ].join(''))}
  </div>
  <div>
    ${section('Address', [
      row('Permanent Address', tenant.permanentAddress),
      row('City', tenant.city),
      row('State', tenant.state),
      row('Pincode', tenant.pincode),
    ].join(''))}
    ${section('Emergency Contact', [
      row('Name', tenant.emergencyContactName),
      row('Phone', tenant.emergencyContactPhone),
      row('Relation', tenant.emergencyContactRel),
    ].join(''))}
    ${section('Parents', [
      row('Father\'s Name', tenant.fatherName),
      row('Father\'s Contact', tenant.fatherContact),
      row('Father\'s Occupation', tenant.fatherOccupation),
      row('Mother\'s Name', tenant.motherName),
      row('Mother\'s Contact', tenant.motherContact),
      row('Mother\'s Occupation', tenant.motherOccupation),
    ].join(''))}
  </div>
</div>
${tenant.notes ? `<hr/><p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#9ca3af;margin-bottom:6px">Notes</p><p style="font-size:13px">${tenant.notes}</p>` : ''}
${(tenant.govIds ?? []).length > 0 ? `<hr/><p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#9ca3af;margin-bottom:8px">Government IDs</p>${(tenant.govIds ?? []).map((g, i) => `<div style="margin-bottom:12px"><p style="font-size:11px;font-weight:600;color:#0d9488;margin-bottom:6px">ID ${i + 1}${g.govIdType ? ' — ' + g.govIdType : ''}</p><div style="display:flex;gap:12px">${g.frontPhotoUrl ? `<div><p style="font-size:10px;color:#6b7280;margin-bottom:3px">Front</p><img src="${g.frontPhotoUrl}" style="max-width:200px;max-height:140px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb"/></div>` : ''}${g.backPhotoUrl ? `<div><p style="font-size:10px;color:#6b7280;margin-bottom:3px">Back</p><img src="${g.backPhotoUrl}" style="max-width:200px;max-height:140px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb"/></div>` : ''}</div></div>`).join('')}` : ''}
<hr/>
<p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#9ca3af;margin-bottom:10px">Tenant Agreement</p>
${tenant.termsAccepted ? `
<table style="border-collapse:collapse;width:100%;margin-bottom:12px">
  <tr>
    <td style="color:#6b7280;padding:5px 12px 5px 0;font-size:12px;white-space:nowrap;vertical-align:top">Status</td>
    <td style="padding:5px 0;font-size:13px;font-weight:500"><span style="display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:600;background:#d1fae5;color:#065f46">Accepted</span></td>
  </tr>
  <tr>
    <td style="color:#6b7280;padding:5px 12px 5px 0;font-size:12px;white-space:nowrap;vertical-align:top">Accepted On</td>
    <td style="padding:5px 0;font-size:13px;font-weight:500">${tenant.termsAcceptedAt ? new Date(tenant.termsAcceptedAt).toLocaleString('en-IN') : '—'}</td>
  </tr>
  <tr>
    <td style="color:#6b7280;padding:5px 12px 5px 0;font-size:12px;white-space:nowrap;vertical-align:top">Declaration</td>
    <td style="padding:5px 0;font-size:12px;color:#374151">The above mentioned details are correct. I have read and agree to the Terms &amp; Conditions and Privacy Policy of hostel management.</td>
  </tr>
</table>
${tenant.termsSignature ? `<div><p style="font-size:11px;color:#6b7280;margin-bottom:6px">Tenant Signature</p><div style="border:1px solid #e5e7eb;border-radius:6px;padding:8px;display:inline-block;background:#fafafa"><img src="${tenant.termsSignature}" style="max-width:320px;max-height:120px;display:block" alt="Signature"/></div></div>` : ''}
` : `<p style="font-size:13px;color:#6b7280;font-style:italic">Terms not yet accepted by tenant.</p>`}
<div class="footer">
  <p>Generated on ${new Date().toLocaleString('en-IN')} · PG Management System</p>
</div>
</body></html>`;

  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 400);
}

function ProfileTab({ tenant, editing, setEditing }: { tenant: Tenant; editing: boolean; setEditing: (v: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<UpdateTenantRequest>({
    fullName: tenant.fullName,
    phone: tenant.phone,
    email: tenant.email ?? '',
    dateOfBirth: tenant.dateOfBirth ?? '',
    gender: tenant.gender,
    occupation: tenant.occupation ?? '',
    companyOrInstitution: tenant.companyOrInstitution ?? '',
    permanentAddress: tenant.permanentAddress ?? '',
    city: tenant.city ?? '',
    state: tenant.state ?? '',
    pincode: tenant.pincode ?? '',
    emergencyContactName: tenant.emergencyContactName ?? '',
    emergencyContactPhone: tenant.emergencyContactPhone ?? '',
    emergencyContactRel: tenant.emergencyContactRel ?? '',
    govIdType: tenant.govIdType,
    govIdNumber: tenant.govIdNumber ?? '',
    expectedCheckoutDate: tenant.expectedCheckoutDate ?? '',
    monthlyRent: tenant.monthlyRent,
    securityDeposit: tenant.securityDeposit,
    notes: tenant.notes ?? '',
    fatherName: tenant.fatherName ?? '',
    fatherContact: tenant.fatherContact ?? '',
    fatherOccupation: tenant.fatherOccupation ?? '',
    motherName: tenant.motherName ?? '',
    motherContact: tenant.motherContact ?? '',
    motherOccupation: tenant.motherOccupation ?? '',
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        dateOfBirth: form.dateOfBirth || null,
        expectedCheckoutDate: form.expectedCheckoutDate || null,
      };
      return client.put(`/api/tenants/${tenant.id}`, body).then(r => r as unknown as Tenant);
    },
    onSuccess: (updated) => {
      qc.setQueryData<Tenant>(['tenant', tenant.id], (old) => ({
        ...(old ?? {}),
        ...(updated as Tenant),
        // Never let the profile save wipe the photo — it's managed by the PATCH endpoint
        photoUrl: (updated as Tenant).photoUrl ?? old?.photoUrl,
      }));
      setEditing(false);
    },
  });

  const inp = 'border border-gray-300 rounded-lg px-3 py-2 text-sm w-full';

  if (editing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">{t('owner.editProfile', 'Edit Profile')}</h3>
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 border border-gray-300 rounded-lg">
              <X size={14} /> {t('common.cancel')}
            </button>
            <button onClick={() => save.mutate()} disabled={save.isPending}
              className="flex items-center gap-1 text-sm bg-teal-600 text-white px-3 py-1.5 rounded-lg hover:bg-teal-700 disabled:opacity-50">
              <Check size={14} /> {save.isPending ? t('owner.saving', 'Saving…') : t('common.save')}
            </button>
          </div>
        </div>

        {/* Photo — uploads immediately on select, independent of form save */}
        <div className="flex justify-center pb-2 border-b border-gray-100">
          <PhotoUploader tenantId={tenant.id} photoUrl={tenant.photoUrl ?? ''} name={form.fullName} />
        </div>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.personal', 'Personal')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs text-gray-500">{t('tenant.fullName')} *</label><input className={inp} value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('common.phone')} *</label><input type="tel" inputMode="tel" className={inp} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: sanitizePhoneInput(e.target.value) }))} /></div>
            <div><label className="text-xs text-gray-500">{t('common.email')}</label><input className={inp} value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.dateOfBirth', 'Date of Birth')}</label><input type="date" className={inp} value={form.dateOfBirth ?? ''} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.gender', 'Gender')}</label>
              <Select
                value={form.gender ?? ''}
                onChange={e => setForm(f => ({ ...f, gender: e.target.value as Gender || undefined }))}
                options={[
                  { value: '', label: '—' },
                  { value: 'MALE', label: t('owner.male', 'Male') },
                  { value: 'FEMALE', label: t('owner.female', 'Female') },
                  { value: 'OTHER', label: t('owner.other', 'Other') },
                ]}
              />
            </div>
            <div><label className="text-xs text-gray-500">{t('owner.occupation', 'Occupation')}</label><input className={inp} value={form.occupation ?? ''} onChange={e => setForm(f => ({ ...f, occupation: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.companyOrInstitution', 'Company / Institution')}</label><input className={inp} value={form.companyOrInstitution ?? ''} onChange={e => setForm(f => ({ ...f, companyOrInstitution: e.target.value }))} /></div>
          </div>
        </section>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('common.address')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs text-gray-500">{t('owner.permanentAddress', 'Permanent Address')}</label><textarea className={inp} rows={2} value={form.permanentAddress ?? ''} onChange={e => setForm(f => ({ ...f, permanentAddress: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.city', 'City')}</label><input className={inp} value={form.city ?? ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.state', 'State')}</label><input className={inp} value={form.state ?? ''} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.pincode', 'Pincode')}</label><input className={inp} value={form.pincode ?? ''} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} /></div>
          </div>
        </section>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.financials', 'Financials')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-gray-500">{t('tenant.monthlyRent')} *</label><input type="number" className={inp} value={form.monthlyRent} onChange={e => setForm(f => ({ ...f, monthlyRent: Number(e.target.value) }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.securityDeposit', 'Security Deposit')}</label><input type="number" className={inp} value={form.securityDeposit ?? ''} onChange={e => setForm(f => ({ ...f, securityDeposit: Number(e.target.value) }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.expectedCheckout', 'Expected Checkout')}</label><input type="date" className={inp} value={form.expectedCheckoutDate ?? ''} onChange={e => setForm(f => ({ ...f, expectedCheckoutDate: e.target.value }))} /></div>
          </div>
        </section>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.governmentIds', 'Government IDs')}</p>
          <GovIdList tenantId={tenant.id} govIds={tenant.govIds ?? []} />
        </section>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.emergencyContact', 'Emergency Contact')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-gray-500">{t('common.name')}</label><input className={inp} value={form.emergencyContactName ?? ''} onChange={e => setForm(f => ({ ...f, emergencyContactName: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('common.phone')}</label><input type="tel" inputMode="tel" className={inp} value={form.emergencyContactPhone ?? ''} onChange={e => setForm(f => ({ ...f, emergencyContactPhone: sanitizePhoneInput(e.target.value) }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.relation', 'Relation')}</label><input className={inp} value={form.emergencyContactRel ?? ''} onChange={e => setForm(f => ({ ...f, emergencyContactRel: e.target.value }))} /></div>
          </div>
        </section>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.fathersDetails', "Father's Details")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs text-gray-500">{t('owner.fathersName', "Father's Name")}</label><input className={inp} value={form.fatherName ?? ''} onChange={e => setForm(f => ({ ...f, fatherName: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.fathersContact', "Father's Contact")}</label><input type="tel" inputMode="tel" className={inp} value={form.fatherContact ?? ''} onChange={e => setForm(f => ({ ...f, fatherContact: sanitizePhoneInput(e.target.value) }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.fathersOccupation', "Father's Occupation")}</label><input className={inp} value={form.fatherOccupation ?? ''} onChange={e => setForm(f => ({ ...f, fatherOccupation: e.target.value }))} /></div>
          </div>
        </section>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.mothersDetails', "Mother's Details")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs text-gray-500">{t('owner.mothersName', "Mother's Name")}</label><input className={inp} value={form.motherName ?? ''} onChange={e => setForm(f => ({ ...f, motherName: e.target.value }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.mothersContact', "Mother's Contact")}</label><input type="tel" inputMode="tel" className={inp} value={form.motherContact ?? ''} onChange={e => setForm(f => ({ ...f, motherContact: sanitizePhoneInput(e.target.value) }))} /></div>
            <div><label className="text-xs text-gray-500">{t('owner.mothersOccupation', "Mother's Occupation")}</label><input className={inp} value={form.motherOccupation ?? ''} onChange={e => setForm(f => ({ ...f, motherOccupation: e.target.value }))} /></div>
          </div>
        </section>

        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.notes', 'Notes')}</p>
          <textarea className={inp} rows={3} value={form.notes ?? ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </section>

        {save.isError && (
          <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {t('owner.saveFailed', 'Save failed')}: {(save.error as Error)?.message ?? t('owner.pleaseTryAgain', 'Please try again.')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.personal', 'Personal')}</p>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('tenant.fullName')} value={tenant.fullName} />
          <Field label={t('common.phone')} value={tenant.phone} />
          <Field label={t('common.email')} value={tenant.email} />
          <Field label={t('owner.dateOfBirth', 'Date of Birth')} value={tenant.dateOfBirth} />
          <Field label={t('owner.gender', 'Gender')} value={tenant.gender} />
          <Field label={t('owner.occupation', 'Occupation')} value={tenant.occupation} />
          <Field label={t('owner.companyOrInstitution', 'Company / Institution')} value={tenant.companyOrInstitution} />
        </div>
      </section>
      <section>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('common.address')}</p>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('owner.permanentAddress', 'Permanent Address')} value={tenant.permanentAddress} />
          <Field label={t('owner.city', 'City')} value={tenant.city} />
          <Field label={t('owner.state', 'State')} value={tenant.state} />
          <Field label={t('owner.pincode', 'Pincode')} value={tenant.pincode} />
        </div>
      </section>
      <section>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.financials', 'Financials')}</p>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('tenant.monthlyRent')} value={tenant.monthlyRent != null ? `₹${Number(tenant.monthlyRent).toLocaleString()}` : undefined} />
          <Field label={t('owner.securityDeposit', 'Security Deposit')} value={tenant.securityDeposit != null ? `₹${Number(tenant.securityDeposit).toLocaleString()}` : undefined} />
          <Field label={t('tenant.joiningDate')} value={tenant.joiningDate} />
          <Field label={t('owner.expectedCheckout', 'Expected Checkout')} value={tenant.expectedCheckoutDate} />
        </div>
      </section>
      {(tenant.govIds ?? []).length > 0 && (
        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.governmentIds', 'Government IDs')}</p>
          <div className="space-y-4">
            {(tenant.govIds ?? []).map((g, i) => (
              <div key={g.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
                {g.govIdType && <p className="text-xs font-semibold text-teal-700 mb-2">{t('owner.idNumber', 'ID {{num}}', { num: i + 1 })} — {g.govIdType}</p>}
                <div className="grid grid-cols-2 gap-3">
                  {g.frontPhotoUrl && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t('owner.front', 'Front')}</p>
                      <img src={g.frontPhotoUrl} alt="ID Front" className="rounded-lg border border-gray-200 object-contain bg-white w-full" style={{ maxHeight: 150 }} />
                    </div>
                  )}
                  {g.backPhotoUrl && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t('owner.back', 'Back')}</p>
                      <img src={g.backPhotoUrl} alt="ID Back" className="rounded-lg border border-gray-200 object-contain bg-white w-full" style={{ maxHeight: 150 }} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {(tenant.emergencyContactName || tenant.emergencyContactPhone) && (
        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.emergencyContact', 'Emergency Contact')}</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('common.name')} value={tenant.emergencyContactName} />
            <Field label={t('common.phone')} value={tenant.emergencyContactPhone} />
            <Field label={t('owner.relation', 'Relation')} value={tenant.emergencyContactRel} />
          </div>
        </section>
      )}
      <section>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.parentsDetails', 'Parents Details')}</p>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 mb-1">{t('owner.father', 'Father')}</p>
            <Field label={t('common.name')} value={tenant.fatherName} />
            <Field label={t('owner.contact', 'Contact')} value={tenant.fatherContact} />
            <Field label={t('owner.occupation', 'Occupation')} value={tenant.fatherOccupation} />
            {!tenant.fatherName && !tenant.fatherContact && <p className="text-xs text-gray-300 italic">{t('owner.notAdded', 'Not added')}</p>}
          </div>
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 mb-1">{t('owner.mother', 'Mother')}</p>
            <Field label={t('common.name')} value={tenant.motherName} />
            <Field label={t('owner.contact', 'Contact')} value={tenant.motherContact} />
            <Field label={t('owner.occupation', 'Occupation')} value={tenant.motherOccupation} />
            {!tenant.motherName && !tenant.motherContact && <p className="text-xs text-gray-300 italic">{t('owner.notAdded', 'Not added')}</p>}
          </div>
        </div>
      </section>
      {tenant.notes && (
        <section>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">{t('owner.notes', 'Notes')}</p>
          <p className="text-sm text-gray-600 whitespace-pre-line">{tenant.notes}</p>
        </section>
      )}
    </div>
  );
}


function printInvoice(tenant: Tenant, hostel: Hostel | undefined, entry: RentEntry) {
  const balance = Number(entry.amountDue) - Number(entry.amountPaid);
  const hostelName = hostel?.name ?? 'Hostel';
  const hostelAddr = [hostel?.address, hostel?.city, hostel?.state, hostel?.pincode].filter(Boolean).join(', ');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Invoice ${entry.billingMonth} – ${tenant.fullName}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #111; padding: 40px; }
  h1 { font-size: 22px; color: #1E3A5F; margin-bottom: 4px; }
  .sub { color: #555; font-size: 12px; }
  hr { border: none; border-top: 1px solid #ddd; margin: 18px 0; }
  .row { display: flex; justify-content: space-between; margin-bottom: 6px; }
  .label { color: #555; }
  .amount { font-weight: bold; }
  .status { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 11px; font-weight: bold;
    background: ${entry.status === 'PAID' ? '#d1fae5' : entry.status === 'OVERDUE' ? '#fee2e2' : '#fef3c7'};
    color: ${entry.status === 'PAID' ? '#065f46' : entry.status === 'OVERDUE' ? '#991b1b' : '#92400e'}; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  th { background: #f1f5f9; text-align: left; padding: 8px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; }
  .total-row td { font-weight: bold; border-top: 2px solid #cbd5e1; background: #f8fafc; }
  .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #888; }
  @media print { body { padding: 20px; } }
</style></head><body>
<h1>${hostelName}</h1>
<p class="sub">${hostelAddr}</p>
${hostel?.phone ? `<p class="sub">Phone: ${hostel.phone}</p>` : ''}
${hostel?.email ? `<p class="sub">Email: ${hostel.email}</p>` : ''}

<hr/>

<div style="display:flex;justify-content:space-between;align-items:flex-start">
  <div>
    <p style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">Bill To</p>
    <p style="font-size:15px;font-weight:bold;margin-top:4px">${tenant.fullName}</p>
    <p class="sub">${tenant.phone}</p>
    ${tenant.email ? `<p class="sub">${tenant.email}</p>` : ''}
  </div>
  <div style="text-align:right">
    <p style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">Invoice</p>
    <p style="font-size:15px;font-weight:bold;margin-top:4px">RENT-${entry.billingMonth.replace('-', '')}</p>
    <p class="sub">Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
  </div>
</div>

<hr/>

<table>
  <thead><tr><th>Description</th><th>Period</th><th>Due Date</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>
    <tr>
      <td>Monthly Rent</td>
      <td>${entry.billingMonth}</td>
      <td>${entry.dueDate}</td>
      <td style="text-align:right">₹${Number(entry.amountDue).toLocaleString('en-IN')}</td>
    </tr>
    <tr>
      <td colspan="3" style="text-align:right;color:#059669">Amount Paid</td>
      <td style="text-align:right;color:#059669">– ₹${Number(entry.amountPaid).toLocaleString('en-IN')}</td>
    </tr>
    <tr class="total-row">
      <td colspan="3" style="text-align:right">Balance Due</td>
      <td style="text-align:right;color:${balance > 0 ? '#dc2626' : '#059669'}">₹${balance.toLocaleString('en-IN')}</td>
    </tr>
  </tbody>
</table>

<div style="margin-top:20px">
  Status: <span class="status">${entry.status}</span>
</div>

<div class="footer">
  <p>This is a computer-generated invoice. No signature required.</p>
  <p style="margin-top:4px">${hostelName} • ${hostelAddr}</p>
</div>
</body></html>`;

  const win = window.open('', '_blank', 'width=800,height=700');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 400);
}

const PAYMENT_ROLE_LABELS: Record<string, string> = {
  HOSTEL_OWNER: 'Owner', SUPERVISOR: 'Supervisor', ADMIN: 'Admin',
};

function PaymentDetailRow({ rentEntryId }: { rentEntryId: string }) {
  const { t } = useTranslation();
  const { data: payments = [], isLoading } = useQuery<Payment[]>({
    queryKey: ['entry-payments', rentEntryId],
    queryFn: () => client.get(`/api/rent/${rentEntryId}/payments`).then(r => r as unknown as Payment[]),
  });
  if (isLoading) return <tr><td colSpan={6} className="px-4 py-2 text-xs text-gray-400">{t('common.loading')}</td></tr>;
  if (!payments.length) return null;
  return (
    <>
      {payments.map(p => (
        <tr key={p.id} className="bg-teal-50/40">
          <td colSpan={6} className="px-6 py-2">
            <div className="flex items-center gap-4 text-xs text-gray-600 flex-wrap">
              <span className="font-medium text-teal-700">{t('owner.amountCollected', '₹{{amount}} collected', { amount: Number(p.amount).toLocaleString() })}</span>
              <span className="text-gray-400">{new Date(p.paidAt).toLocaleString()}</span>
              <span className="text-gray-500">{p.paymentMethod?.replace(/_/g, ' ')}</span>
              {p.transactionRef && <span className="text-gray-400">{t('owner.refLabel', 'Ref')}: {p.transactionRef}</span>}
              {p.recordedByName && (
                <span className="flex items-center gap-1">
                  <span className="text-gray-400">{t('owner.collectedBy', 'Collected by')}</span>
                  <span className="font-semibold text-gray-800">{p.recordedByName}</span>
                  {p.recordedByRole && (
                    <span className="bg-teal-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                      {PAYMENT_ROLE_LABELS[p.recordedByRole] ?? p.recordedByRole}
                    </span>
                  )}
                </span>
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

function RentTab({ tenantId, tenant, hostel }: { tenantId: string; tenant: Tenant; hostel?: Hostel }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: entries = [], isLoading } = useQuery<RentEntry[]>({
    queryKey: ['tenant-rent', tenantId],
    queryFn: () => client.get(`/api/rent/tenant/${tenantId}`).then(r => r as unknown as RentEntry[]),
  });

  const payableEntries = entries.filter(e => e.status === 'PENDING' || e.status === 'PARTIAL' || e.status === 'OVERDUE');

  const { page, size, setPage } = usePagination();
  const pagedEntries = paginateArray(entries, page, size);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payForm, setPayForm] = useState({ amount: '', paymentMethod: 'CASH', transactionRef: '' });
  const payRefRequired = payForm.paymentMethod === 'UPI' || payForm.paymentMethod === 'BANK_TRANSFER';
  const autoOpened = useRef(false);

  const openPay = (e: RentEntry) => {
    setPayingId(e.id);
    setPayForm({ amount: String(Number(e.amountDue) - Number(e.amountPaid)), paymentMethod: 'CASH', transactionRef: '' });
  };

  // Just-admitted/checked-in tenants land here with exactly one fresh charge — open
  // the payment dialog automatically so rent can be collected immediately, like a
  // hotel front desk, instead of requiring an extra click to find it.
  useEffect(() => {
    if (!autoOpened.current && entries.length === 1 && payableEntries.length === 1) {
      autoOpened.current = true;
      openPay(payableEntries[0]);
    }
  }, [entries, payableEntries]);

  const payMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { amount: number; paymentMethod: string; transactionRef?: string } }) =>
      client.post(`/api/rent/${id}/pay`, body),
    onSuccess: () => {
      setPayingId(null);
      void qc.invalidateQueries({ queryKey: ['tenant-rent', tenantId] });
    },
  });

  const backfillMutation = useMutation({
    mutationFn: () => client.post(`/api/tenants/${tenantId}/backfill-rent`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenant-rent', tenantId] }),
  });

  if (isLoading) return <p className="text-gray-400 py-8 text-center">{t('common.loading')}</p>;
  if (!entries.length) return (
    <div className="text-center py-10 text-gray-400">
      <Receipt size={36} className="mx-auto mb-3 text-gray-200" />
      <p>{t('owner.noRentHistory', 'No rent history yet.')}</p>
      <button
        onClick={() => backfillMutation.mutate()}
        disabled={backfillMutation.isPending}
        className="mt-3 text-sm text-teal-600 hover:text-teal-800 hover:underline disabled:opacity-50"
      >
        {backfillMutation.isPending ? t('owner.generating', 'Generating…') : t('owner.generateRentForJoiningDate', 'Generate rent for joining date')}
      </button>
      {backfillMutation.isError && <p className="text-red-600 text-xs mt-2">{(backfillMutation.error as Error).message}</p>}
    </div>
  );

  const total = entries.reduce((s, e) => s + Number(e.amountDue), 0);
  const paid  = entries.reduce((s, e) => s + Number(e.amountPaid), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => backfillMutation.mutate()}
          disabled={backfillMutation.isPending}
          title={t('owner.backfillHint', 'Generate any missing rent entries between the joining date and now')}
          className="text-xs text-teal-600 hover:text-teal-800 hover:underline disabled:opacity-50"
        >
          {backfillMutation.isPending ? t('owner.checking', 'Checking…') : t('owner.backfillMissingMonths', 'Backfill missing months')}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-400">{t('owner.totalCharged', 'Total Charged')}</p>
          <p className="font-bold text-gray-900">₹{total.toLocaleString()}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3">
          <p className="text-xs text-gray-400">{t('rent.totalPaid')}</p>
          <p className="font-bold text-green-700">₹{paid.toLocaleString()}</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3">
          <p className="text-xs text-gray-400">{t('dashboard.outstanding')}</p>
          <p className="font-bold text-red-700">₹{(total - paid).toLocaleString()}</p>
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-2 text-left">{t('owner.month', 'Month')}</th>
              <th className="px-4 py-2 text-left">{t('rent.dueDate')}</th>
              <th className="px-4 py-2 text-right">{t('rent.amountDue')}</th>
              <th className="px-4 py-2 text-right">{t('owner.paid', 'Paid')}</th>
              <th className="px-4 py-2 text-left">{t('common.status')}</th>
              <th className="px-4 py-2 text-center">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pagedEntries.map(e => {
              const hasPaid = e.status === 'PAID' || e.status === 'PARTIAL' || e.status === 'WAIVED';
              const expanded = expandedId === e.id;
              return (
                <>
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium">
                      {hasPaid ? (
                        <button
                          onClick={() => setExpandedId(expanded ? null : e.id)}
                          className="flex items-center gap-1 text-left hover:text-teal-700 transition"
                          title={t('owner.viewPaymentDetails', 'View payment details')}
                        >
                          <span className={`text-xs transition-transform ${expanded ? 'rotate-90' : ''}`}>▶</span>
                          {e.billingMonth}
                        </button>
                      ) : e.billingMonth}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{e.dueDate}</td>
                    <td className="px-4 py-2.5 text-right">₹{Number(e.amountDue).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-green-700">₹{Number(e.amountPaid).toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RENT_STATUS_COLORS[e.status] ?? 'bg-gray-100 text-gray-600'}`}>{e.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {(e.status === 'PENDING' || e.status === 'PARTIAL' || e.status === 'OVERDUE') && (
                          <button
                            onClick={() => openPay(e)}
                            className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 hover:bg-teal-50 px-2 py-1 rounded-md transition font-medium"
                          >
                            {t('rent.recordPayment')}
                          </button>
                        )}
                        <button
                          onClick={() => printInvoice(tenant, hostel, e)}
                          title={t('owner.downloadInvoice', 'Download Invoice')}
                          className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 rounded-md transition"
                        >
                          <Download size={13} /> {t('owner.pdf', 'PDF')}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded && <PaymentDetailRow rentEntryId={e.id} />}
                </>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={page}
          totalPages={Math.ceil(entries.length / size) || 0}
          totalElements={entries.length}
          size={size}
          onPageChange={setPage}
          className="px-4 pb-3"
        />
      </div>

      {payingId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPayingId(null)}>
          <div className="bg-chrome-page rounded-xl p-6 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">{t('rent.recordPayment')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.amount')} *</label>
                <input
                  type="number"
                  value={payForm.amount}
                  onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('rent.paymentMethod')}</label>
                <Select
                  value={payForm.paymentMethod}
                  onChange={e => setPayForm(p => ({ ...p, paymentMethod: e.target.value }))}
                  options={['CASH', 'UPI', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE', 'OTHER'].map(m => ({ value: m, label: paymentMethodLabel(t, m) }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('owner.transactionRef', 'Transaction / Reference ID')}{payRefRequired && ' *'}
                </label>
                <input
                  value={payForm.transactionRef}
                  onChange={e => setPayForm(p => ({ ...p, transactionRef: e.target.value }))}
                  placeholder={payRefRequired
                    ? t('owner.upiRefPlaceholderRequired', 'Enter unique UPI / bank transaction ID…')
                    : t('owner.upiRefPlaceholder', 'UPI ID / Ref number…')}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                {payRefRequired && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    {t('owner.transactionRefHint', 'Must be unique — the same reference cannot be used for two payments.')}
                  </p>
                )}
              </div>
            </div>
            {payMutation.isError && (
              <p className="text-red-500 text-xs mt-2">{(payMutation.error as Error).message}</p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => payMutation.mutate({
                  id: payingId,
                  body: { amount: Number(payForm.amount), paymentMethod: payForm.paymentMethod, transactionRef: payForm.transactionRef || undefined },
                })}
                disabled={!payForm.amount || (payRefRequired && !payForm.transactionRef.trim()) || payMutation.isPending}
                className="flex-1 bg-teal-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
              >
                {payMutation.isPending ? t('owner.saving', 'Saving…') : t('common.save')}
              </button>
              <button onClick={() => setPayingId(null)} className="flex-1 border rounded-lg py-2 text-sm">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  fullName:              'Full Name',
  phone:                 'Phone',
  email:                 'Email',
  dateOfBirth:           'Date of Birth',
  gender:                'Gender',
  occupation:            'Occupation',
  companyOrInstitution:  'Company / Institution',
  permanentAddress:      'Permanent Address',
  city:                  'City',
  state:                 'State',
  pincode:               'Pincode',
  monthlyRent:           'Monthly Rent',
  securityDeposit:       'Security Deposit',
  expectedCheckoutDate:  'Expected Checkout',
  status:                'Status',
  notes:                 'Notes',
  govIdType:             'Gov ID Type',
  govIdNumber:           'Gov ID Number',
  emergencyContactName:  'Emergency Contact Name',
  emergencyContactPhone: 'Emergency Contact Phone',
  emergencyContactRel:   'Emergency Contact Relation',
  fatherName:            "Father's Name",
  fatherContact:         "Father's Contact",
  fatherOccupation:      "Father's Occupation",
  motherName:            "Mother's Name",
  motherContact:         "Mother's Contact",
  motherOccupation:      "Mother's Occupation",
  photoUrl:              'Photo',
};

const ROLE_LABELS: Record<string, string> = {
  HOSTEL_OWNER: 'Owner', SUPERVISOR: 'Supervisor', ADMIN: 'Admin', TENANT: 'Tenant',
};

const MONEY_FIELDS = new Set(['monthlyRent', 'securityDeposit']);
const ENUM_LABELS: Record<string, Record<string, string>> = {
  status:    { ACTIVE: 'Active', RESERVED: 'Reserved', CHECKED_OUT: 'Checked Out', CANCELLED: 'Cancelled' },
  gender:    { MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' },
  govIdType: { AADHAAR: 'Aadhaar', PASSPORT: 'Passport', DRIVING_LICENSE: 'Driving License', VOTER_ID: 'Voter ID', NATIONAL_ID: 'National ID', OTHER: 'Other' },
};

function humanValue(key: string, raw: string): string {
  if (!raw || raw === 'null' || raw === 'undefined') return '';
  if (MONEY_FIELDS.has(key)) {
    const n = Number(raw);
    return isNaN(n) ? raw : `₹${n.toLocaleString('en-IN')}`;
  }
  if (ENUM_LABELS[key]?.[raw]) return ENUM_LABELS[key][raw];
  return raw;
}

function toSentence(key: string, label: string, from: string, to: string): string {
  if (key === 'photoUrl') return 'Profile photo was updated';
  const f = humanValue(key, from);
  const t = humanValue(key, to);
  if (!f && t)  return `${label} set to "${t}"`;
  if (f && !t)  return `${label} was removed`;
  if (MONEY_FIELDS.has(key)) {
    const nf = Number(from), nt = Number(to);
    if (!isNaN(nf) && !isNaN(nt))
      return `${label} ${nt > nf ? 'increased' : 'decreased'} from ${f} to ${t}`;
  }
  return `${label} changed from "${f}" to "${t}"`;
}

function diffFields(before: string, after: string): { key: string; label: string; from: string; to: string; sentence: string }[] {
  try {
    const b = JSON.parse(before);
    const a = JSON.parse(after);
    const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
    const changes: { key: string; label: string; from: string; to: string; sentence: string }[] = [];
    keys.forEach(k => {
      const bv = String(b[k] ?? '');
      const av = String(a[k] ?? '');
      if (bv === av) return;
      const label = FIELD_LABELS[k] ?? k;
      changes.push({ key: k, label, from: bv, to: av, sentence: toSentence(k, label, bv, av) });
    });
    return changes;
  } catch { return []; }
}

function AuditTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { data: entries = [], isLoading } = useQuery<AuditLogEntry[]>({
    queryKey: ['tenant-audit', tenantId],
    queryFn: () => client.get(`/api/audit/entity/${tenantId}`).then(r => r as unknown as AuditLogEntry[]),
  });

  if (isLoading) return <p className="text-gray-400 py-8 text-center">{t('common.loading')}</p>;
  if (!entries.length) return (
    <div className="text-center py-10 text-gray-400">
      <Clock size={36} className="mx-auto mb-3 text-gray-200" />
      <p>{t('owner.noAuditHistory', 'No audit history yet.')}</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {entries.map(e => {
        const changes = e.beforeValue && e.afterValue ? diffFields(e.beforeValue, e.afterValue) : [];
        const roleName = e.changedByRole ? (ROLE_LABELS[e.changedByRole] ?? e.changedByRole) : null;
        const initials = e.changedByName ? e.changedByName.charAt(0).toUpperCase() : '?';
        return (
          <div key={e.id} className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
            {/* Who + when */}
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-sm font-bold shrink-0">
                  {initials}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 leading-tight">
                    {e.changedByName ?? t('owner.system', 'System')}
                    {roleName && (
                      <span className="ml-1.5 text-xs font-medium text-white bg-teal-600 px-1.5 py-0.5 rounded-full">
                        {roleName}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">{e.action.replace(/_/g, ' ')}</p>
                </div>
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                {new Date(e.createdAt).toLocaleString()}
              </span>
            </div>

            {/* Changed fields — plain English */}
            {changes.length > 0 && (
              <ul className="mt-1 space-y-1 border-t border-gray-100 pt-3">
                {changes.map(c => (
                  <li key={c.key} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                    {c.sentence}
                  </li>
                ))}
              </ul>
            )}

            {e.details && <p className="mt-2 text-xs text-gray-500">{e.details}</p>}
          </div>
        );
      })}
    </div>
  );
}

const COMPLAINT_STATUS_COLORS: Record<string, string> = {
  OPEN:        'bg-red-100 text-red-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  RESOLVED:    'bg-green-100 text-green-800',
  CANCELLED:   'bg-orange-100 text-orange-700',
};

const COMPLAINT_CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  ROOM:              'Room',
  CORRIDOR:          'Corridor / Common Area',
  WIFI:              'WiFi / Internet',
  ELECTRICITY_WATER: 'Electricity / Water',
  OTHER:             'Other',
};

const COMPLAINT_CATEGORY_LABEL_KEYS: Record<ComplaintCategory, string> = {
  ROOM:              'owner.categoryRoom',
  CORRIDOR:          'owner.categoryCorridor',
  WIFI:              'owner.categoryWifi',
  ELECTRICITY_WATER: 'owner.categoryElectricityWater',
  OTHER:             'owner.categoryOther',
};

const COMPLAINT_STATUS_OPTIONS: { value: ComplaintStatus; label: string; labelKey: string }[] = [
  { value: 'OPEN',        label: 'Open',        labelKey: 'complaint.open' },
  { value: 'IN_PROGRESS', label: 'In Progress', labelKey: 'complaint.inProgress' },
  { value: 'RESOLVED',    label: 'Resolved',    labelKey: 'complaint.resolved' },
  { value: 'CANCELLED',   label: 'Cancelled',   labelKey: 'common.cancelled' },
];

function RaiseComplaintOnBehalfPanel({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{ category: ComplaintCategory; title: string; description: string }>({
    category: 'ROOM', title: '', description: '',
  });

  const mutation = useMutation({
    mutationFn: () => client.post('/api/complaints', { ...form, tenantId }),
    onSuccess: () => {
      setShowForm(false);
      setForm({ category: 'ROOM', title: '', description: '' });
      onDone();
    },
  });

  return (
    <div className="mb-3">
      <div className="flex justify-end">
        <button
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg font-medium transition"
        >
          {showForm ? t('common.cancel') : t('complaint.raiseOnBehalf', 'Raise Complaint on Behalf')}
        </button>
      </div>
      {showForm && (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-xs text-amber-900">{t('complaint.raiseOnBehalfNote', 'This tenant does not have a smartphone or login — record the complaint here on their behalf.')}</p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('complaint.category')}</label>
            <Select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value as ComplaintCategory }))}
              options={(Object.entries(COMPLAINT_CATEGORY_LABEL_KEYS) as [ComplaintCategory, string][]).map(([k, labelKey]) => ({ value: k, label: t(labelKey, COMPLAINT_CATEGORY_LABELS[k]) }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('complaint.titleLabel', 'Title')} *</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder={t('complaint.titlePlaceholder', 'e.g. Tap leaking in bathroom')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('complaint.description')} *</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={t('complaint.descriptionPlaceholder', 'Describe the issue in detail…')}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !form.title.trim() || !form.description.trim()}
              className="text-sm bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            >
              {mutation.isPending ? t('common.submitting', 'Submitting…') : t('complaint.raiseOnBehalfSubmit', 'Submit Complaint')}
            </button>
            {mutation.isError && <p className="text-red-600 text-sm">{(mutation.error as Error).message}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function ComplaintTab({ tenantId, hostelId }: { tenantId: string; hostelId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [updating, setUpdating] = useState<string | null>(null);
  const [statusForm, setStatusForm] = useState<{ status: ComplaintStatus; resolutionNotes: string }>({ status: 'IN_PROGRESS', resolutionNotes: '' });

  const { data: complaints = [], isLoading } = useQuery<Complaint[]>({
    queryKey: ['tenant-complaints', tenantId],
    queryFn: () => client.get(`/api/complaints?hostelId=${hostelId}&size=100`).then(r => {
      const page = r as unknown as { content: Complaint[] };
      return page.content.filter((c: Complaint) => c.tenantId === tenantId);
    }),
  });

  const updateMutation = useMutation({
    mutationFn: (id: string) => client.patch(`/api/complaints/${id}/status`, statusForm),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-complaints', tenantId] });
      setUpdating(null);
    },
  });

  const refetchComplaints = () => void qc.invalidateQueries({ queryKey: ['tenant-complaints', tenantId] });

  if (isLoading) return <p className="text-gray-400 py-8 text-center">{t('common.loading')}</p>;

  return (
    <div className="space-y-3">
      <RaiseComplaintOnBehalfPanel tenantId={tenantId} onDone={refetchComplaints} />
      {complaints.length === 0 && <p className="text-gray-400 text-sm text-center py-8">{t('owner.noComplaintsRaised', 'No complaints raised yet.')}</p>}
      {complaints.map(c => (
        <div key={c.id} className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{t(COMPLAINT_CATEGORY_LABEL_KEYS[c.category], COMPLAINT_CATEGORY_LABELS[c.category])}</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${COMPLAINT_STATUS_COLORS[c.status]}`}>{c.status.replace('_', ' ')}</span>
                <span className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleDateString('en-IN')}</span>
              </div>
              <p className="font-medium text-gray-900">{c.title}</p>
              <p className="text-sm text-gray-500 mt-0.5">{c.description}</p>
              {c.resolutionNotes && (
                <p className="text-sm text-green-700 mt-1 bg-green-50 border border-green-100 rounded px-2 py-1">{t('owner.resolutionLabel', 'Resolution')}: {c.resolutionNotes}</p>
              )}
            </div>
            {c.status !== 'RESOLVED' && c.status !== 'CANCELLED' && (
              <button
                onClick={() => { setUpdating(c.id); setStatusForm({ status: 'IN_PROGRESS', resolutionNotes: '' }); }}
                className="text-xs text-teal-600 hover:text-teal-800 border border-teal-200 px-2 py-1 rounded-lg shrink-0"
              >
                {t('owner.update', 'Update')}
              </button>
            )}
          </div>
          {updating === c.id && (
            <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
              <Select
                value={statusForm.status}
                onChange={e => setStatusForm(f => ({ ...f, status: e.target.value as ComplaintStatus }))}
                options={COMPLAINT_STATUS_OPTIONS.filter(o => o.value !== 'OPEN').map(o => ({ value: o.value, label: t(o.labelKey, o.label) }))}
              />
              <textarea
                value={statusForm.resolutionNotes}
                onChange={e => setStatusForm(f => ({ ...f, resolutionNotes: e.target.value }))}
                placeholder={t('owner.resolutionNotesOptional2', 'Resolution notes (optional)')}
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => updateMutation.mutate(c.id)}
                  disabled={updateMutation.isPending}
                  className="text-sm bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                >
                  {updateMutation.isPending ? t('owner.saving', 'Saving…') : t('common.save')}
                </button>
                <button onClick={() => setUpdating(null)} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">{t('common.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const ACTION_BTN = 'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border font-medium transition disabled:opacity-50 disabled:cursor-not-allowed';

export function OwnerTenantDetailPage({ role = 'owner' }: { role?: 'owner' | 'supervisor' | 'admin' }) {
  const { t } = useTranslation();
  const router = useRouter();
  const params = useParams({ strict: false }) as { id?: string; tenantId: string };
  const { tenantId } = params;
  const { tab: initialTab } = useSearch({ strict: false }) as { tab?: string };
  const qc = useQueryClient();
  const [activeTabState, setActiveTabState] = useState<Tab>((initialTab as Tab) ?? 'profile');
  const [showTransfer, setShowTransfer] = useState(false);
  const [showSwap, setShowSwap] = useState(false);
  const [showSignTerms, setShowSignTerms] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinDate, setCheckinDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ checkoutDate: new Date().toISOString().split('T')[0], notes: '', refundDeposit: false });
  const [editing, setEditing] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');

  // Editing only ever applies to the Profile tab — leaving it drops any in-progress edit.
  const setActiveTab = (tabKey: Tab) => {
    setActiveTabState(tabKey);
    if (tabKey !== 'profile') setEditing(false);
  };

  const { data: tenant, isLoading, error } = useQuery<Tenant>({
    queryKey: ['tenant', tenantId],
    queryFn: () => client.get(`/api/tenants/${tenantId}`).then(r => r as unknown as Tenant),
  });

  // Owner/supervisor routes carry the hostel in the URL; the admin route doesn't, so fall
  // back to the hostel embedded in the tenant record once it loads.
  const hostelId = params.id ?? tenant?.hostelId ?? '';

  const { data: hostel } = useQuery<Hostel>({
    queryKey: ['hostel', hostelId],
    queryFn: () => client.get(`/api/hostels/${hostelId}`).then(r => r as unknown as Hostel),
    enabled: !!hostelId,
  });

  const notifyMutation = useMutation({
    mutationFn: () => client.post('/api/notifications/send', { userId: tenant?.userId, title: notifyTitle, message: notifyMessage }),
    onSuccess: () => {
      setNotifying(false);
      setNotifyTitle('');
      setNotifyMessage('');
    },
  });

  const checkinMutation = useMutation({
    mutationFn: () => client.post(`/api/tenants/${tenantId}/checkin`, { joiningDate: checkinDate }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
      void qc.invalidateQueries({ queryKey: ['tenant-rent', tenantId] });
      setShowCheckin(false);
      setActiveTab('rent');
    },
  });

  const tenantsListUrl = role === 'owner' ? `/owner/hostels/${hostelId}/tenants`
    : role === 'supervisor' ? `/supervisor/hostels/${hostelId}/tenants`
    : '/admin/users';

  // Return to whichever page the user actually came from (hostel bed grid, the Tenants
  // list, or the Rent tab) instead of always landing on the hostel-scoped tenants list —
  // that list is a different view from the main Tenants tab, so this used to feel like a
  // teleport. Falls back to the tenants list only when there's no in-app history to unwind
  // (e.g. the profile was opened directly from a bookmarked/shared link).
  const goBack = () => {
    if (window.history.length > 1) router.history.back();
    else window.location.href = tenantsListUrl;
  };

  const cancelReservationMutation = useMutation({
    mutationFn: () => client.delete(`/api/tenants/${tenantId}/reservation`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
      window.location.href = `${tenantsListUrl}?status=CANCELLED`;
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: () => client.post(`/api/tenants/${tenantId}/checkout`, checkoutForm),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: ['beds'] });
      void qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
      setShowCheckout(false);
    },
  });

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'profile',    label: t('tenant.profile'), icon: <User size={15} /> },
    { key: 'rent',       label: t('owner.payments', 'Payments'),   icon: <Receipt size={15} /> },
    { key: 'complaints', label: t('complaint.title'), icon: <AlertCircle size={15} /> },
    { key: 'audit',      label: t('owner.auditLog', 'Audit Log'),  icon: <Clock size={15} /> },
  ];

  if (isLoading) return <p className="text-gray-400 py-16 text-center">{t('common.loading')}</p>;
  if (error || !tenant) return <p className="text-red-500 py-16 text-center">{t('owner.tenantNotFound', 'Tenant not found.')}</p>;

  const accent = role === 'owner'
    ? { link: 'text-teal-600', avatarBg: 'bg-teal-100', avatarText: 'text-teal-700', rentText: 'text-teal-700', checkin: 'text-teal-700 border-teal-400 bg-teal-50 hover:bg-teal-100' }
    : role === 'supervisor'
    ? { link: 'text-violet-600', avatarBg: 'bg-violet-100', avatarText: 'text-violet-700', rentText: 'text-violet-700', checkin: 'text-violet-700 border-violet-400 bg-violet-50 hover:bg-violet-100' }
    : { link: 'text-blue-600', avatarBg: 'bg-blue-100', avatarText: 'text-blue-700', rentText: 'text-blue-700', checkin: 'text-blue-700 border-blue-400 bg-blue-50 hover:bg-blue-100' };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header — identity shown once here; tabs below never repeat name/phone/email/photo */}
      <div className="space-y-3">
        <div className="flex items-start gap-4">
          <button onClick={goBack} className={`${accent.link} text-sm hover:underline mt-1`}>← {t('common.back')}</button>
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <div className={`w-14 h-14 rounded-xl overflow-hidden shrink-0 ${accent.avatarBg} flex items-center justify-center ${accent.avatarText} font-bold text-xl border-2 border-gray-200 shadow-sm`}>
              {tenant.photoUrl
                ? <img src={tenant.photoUrl} alt={tenant.fullName} className="w-full h-full object-cover" />
                : tenant.fullName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{tenant.fullName}</h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TENANT_STATUS_COLORS[tenant.status] ?? 'bg-gray-100'}`}>{tenant.status.replace('_', ' ')}</span>
                <span className="text-xs text-gray-400">{tenant.phone}</span>
                {tenant.email && <span className="text-xs text-gray-400">{tenant.email}</span>}
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-gray-400">{t('tenant.monthlyRent')}</p>
            <p className={`text-xl font-bold ${accent.rentText}`}>₹{Number(tenant.monthlyRent).toLocaleString()}</p>
          </div>
        </div>

        {/* Actions — every tenant action lives in this one row, same size/shape throughout */}
        <div className="flex flex-wrap items-center gap-2">
          {!editing && (
            <button
              onClick={() => { setActiveTabState('profile'); setEditing(true); }}
              className={`${ACTION_BTN} text-teal-600 border-teal-200 hover:bg-teal-50`}
            >
              <Edit3 size={13} /> {t('common.edit')}
            </button>
          )}
          <button
            onClick={() => setNotifying(v => !v)}
            disabled={!tenant.userId}
            title={tenant.userId ? undefined : t('owner.tenantNotSignedUp', "Tenant hasn't signed up yet")}
            className={`${ACTION_BTN} text-amber-700 border-amber-200 hover:bg-amber-50`}
          >
            <Bell size={13} /> {t('owner.notify', 'Notify')}
          </button>
          <button onClick={() => printProfile(tenant)} className={`${ACTION_BTN} text-gray-600 border-gray-200 hover:bg-gray-50`}>
            <Printer size={13} /> {t('owner.printPdf', 'Print / PDF')}
          </button>
          {tenant.status === 'RESERVED' && (
            <button onClick={() => setShowCheckin(v => !v)} className={`${ACTION_BTN} ${accent.checkin}`}>
              <LogIn size={13} /> {t('tenant.checkIn')}
            </button>
          )}
          {tenant.status === 'RESERVED' && (
            <button
              onClick={() => { if (confirm(t('owner.cancelReservationConfirm', 'Cancel this reservation? The bed will be freed.'))) cancelReservationMutation.mutate(); }}
              disabled={cancelReservationMutation.isPending}
              className={`${ACTION_BTN} text-red-600 border-red-300 hover:bg-red-50`}
            >
              <X size={13} /> {cancelReservationMutation.isPending ? t('owner.cancelling', 'Cancelling…') : t('owner.cancelReservation', 'Cancel Reservation')}
            </button>
          )}
          {tenant.status !== 'CHECKED_OUT' && tenant.status !== 'RESERVED' && (
            <button onClick={() => setShowTransfer(v => !v)} className={`${ACTION_BTN} text-blue-600 border-blue-300 hover:bg-blue-50`}>
              <Move size={13} /> {t('owner.transferRoom', 'Transfer Room')}
            </button>
          )}
          {tenant.status !== 'CHECKED_OUT' && tenant.status !== 'RESERVED' && (
            <button onClick={() => setShowSwap(v => !v)} className={`${ACTION_BTN} text-violet-600 border-violet-300 hover:bg-violet-50`}>
              <ArrowLeftRight size={13} /> {t('owner.swapBed', 'Swap Bed')}
            </button>
          )}
          {tenant.status === 'ACTIVE' && (
            <button onClick={() => setShowCheckout(v => !v)} className={`${ACTION_BTN} bg-red-600 hover:bg-red-700 text-white border-red-600`}>
              <LogOut size={13} /> {t('owner.checkOut', 'Check Out')}
            </button>
          )}
          {!tenant.termsAccepted && tenant.status !== 'CHECKED_OUT' && tenant.status !== 'CANCELLED' && (
            <button onClick={() => setShowSignTerms(v => !v)} className={`${ACTION_BTN} text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100`}>
              <Clock size={13} /> {t('tenant.signOnBehalfAction', 'Sign Terms')}
            </button>
          )}
          {role === 'owner' && (
            <Link
              to="/owner/hostels/$id/tenants/$tenantId/encashment"
              params={{ id: hostelId, tenantId }}
              className={`${ACTION_BTN} text-teal-700 border-teal-300 bg-teal-50 hover:bg-teal-100`}
            >
              <FileText size={13} /> {t('owner.rentReceipt', 'Rent Receipt')}
            </Link>
          )}
        </div>
      </div>

      {showCheckin && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.actualJoiningDate', 'Actual Joining Date')} *</label>
            <input type="date" value={checkinDate} onChange={e => setCheckinDate(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <button onClick={() => checkinMutation.mutate()} disabled={checkinMutation.isPending}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
            {checkinMutation.isPending ? t('owner.checkingIn', 'Checking In…') : t('owner.confirmCheckIn', 'Confirm Check-In')}
          </button>
          <button onClick={() => setShowCheckin(false)} className="border px-4 py-1.5 rounded-lg text-sm text-gray-600">{t('common.cancel')}</button>
          {checkinMutation.isError && <p className="w-full text-red-600 text-sm">{(checkinMutation.error as Error).message}</p>}
        </div>
      )}

      {showTransfer && (
        <TransferBedPanel tenant={tenant} hostelId={hostelId} onClose={() => setShowTransfer(false)} />
      )}

      {showSwap && (
        <SwapBedPanel
          hostelId={hostelId}
          lockedTenant={tenant}
          onClose={() => setShowSwap(false)}
          onSuccess={() => setShowSwap(false)}
        />
      )}

      {showCheckout && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <h3 className="font-semibold text-red-900 mb-3">{t('owner.confirmCheckOut', 'Confirm Check-Out')}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.checkOutDate', 'Check-Out Date')} *</label>
              <input type="date" value={checkoutForm.checkoutDate}
                onChange={e => setCheckoutForm(p => ({ ...p, checkoutDate: e.target.value }))}
                className="w-full border rounded-lg px-3 py-1.5 text-sm" />
            </div>
            <div className="flex items-center gap-2 self-end pb-1.5">
              <input type="checkbox" id="refund-detail" checked={checkoutForm.refundDeposit}
                onChange={e => setCheckoutForm(p => ({ ...p, refundDeposit: e.target.checked }))} className="rounded" />
              <label htmlFor="refund-detail" className="text-sm text-gray-700">{t('owner.refundSecurityDeposit', 'Refund security deposit')}</label>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.notes', 'Notes')}</label>
              <textarea rows={2} value={checkoutForm.notes}
                onChange={e => setCheckoutForm(p => ({ ...p, notes: e.target.value }))}
                className="w-full border rounded-lg px-3 py-1.5 text-sm resize-none" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => checkoutMutation.mutate()} disabled={checkoutMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
              {checkoutMutation.isPending ? t('owner.processing', 'Processing…') : t('owner.confirmCheckOut', 'Confirm Check-Out')}
            </button>
            <button onClick={() => setShowCheckout(false)} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.cancel')}</button>
            {checkoutMutation.isError && <p className="text-red-600 text-sm">{(checkoutMutation.error as Error).message}</p>}
          </div>
        </div>
      )}

      {showSignTerms && (
        <SignTermsOnBehalfInline
          tenantId={tenantId}
          hostel={hostel ?? null}
          onDone={() => {
            setShowSignTerms(false);
            qc.setQueryData<Tenant>(['tenant', tenantId], (old) => old ? { ...old, termsAccepted: true } : old);
          }}
        />
      )}

      {notifying && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-amber-900">{t('owner.sendNotificationTo', 'Send notification to {{name}}', { name: tenant.fullName })}</p>
          <input
            value={notifyTitle}
            onChange={e => setNotifyTitle(e.target.value)}
            placeholder={t('owner.titlePlaceholder', 'Title')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <textarea
            value={notifyMessage}
            onChange={e => setNotifyMessage(e.target.value)}
            placeholder={t('owner.messagePlaceholder', 'Message')}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => notifyMutation.mutate()}
              disabled={notifyMutation.isPending || !notifyTitle.trim() || !notifyMessage.trim()}
              className="flex items-center gap-1.5 text-sm bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              <Send size={14} /> {notifyMutation.isPending ? t('owner.sending', 'Sending…') : t('owner.send', 'Send')}
            </button>
            <button onClick={() => setNotifying(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">{t('common.cancel')}</button>
            {notifyMutation.isError && <p className="text-red-600 text-sm">{(notifyMutation.error as Error).message}</p>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex border-b border-gray-100 rounded-t-xl overflow-hidden">
          {tabs.map(tabItem => (
            <button
              key={tabItem.key}
              onClick={() => setActiveTab(tabItem.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition
                ${activeTabState === tabItem.key
                  ? 'border-teal-500 text-teal-700 bg-teal-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            >
              {tabItem.icon} {tabItem.label}
            </button>
          ))}
        </div>
        <div className="p-6 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-240px)]">
          {activeTabState === 'profile'    && <ProfileTab    tenant={tenant} editing={editing} setEditing={setEditing} />}
          {activeTabState === 'rent'       && <RentTab       tenantId={tenantId} tenant={tenant} hostel={hostel} />}
          {activeTabState === 'complaints' && <ComplaintTab  tenantId={tenantId} hostelId={hostelId} />}
          {activeTabState === 'audit'      && <AuditTab      tenantId={tenantId} />}
        </div>
      </div>
    </div>
  );
}
